import { Dialog, Portal, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, forwardRef, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import Card from "../card";
import { ModalContext, type ModalContextState } from "./modalContext";
import { type ModalStackBehavior, useModalStack } from "./modalStack";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";
export type ModalType = "modal" | "overlay";

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: ModalType;
  unmount?: boolean;
  show?: boolean;
  size?: ModalSize;
  scrollable?: boolean;
  centered?: boolean;
  onClose?: () => void;
  onAfterClose?: () => void;
  stackBehavior?: ModalStackBehavior;
}

const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const {
    show = false,
    type = "modal",
    unmount,
    size = "md",
    scrollable,
    centered,
    onClose,
    onAfterClose,
    stackBehavior = "suspend",
    className,
    ...rest
  } = props;

  const initialRef = useRef(null);
  const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);
  const stack = useModalStack(show, stackBehavior);
  const isInactive = stack.isEnabled && !stack.isTopmost;
  const isHiddenByStack =
    stack.isEnabled && (!stack.isRegistered || stack.isSuspended);
  const inertProps = { inert: isInactive ? "" : undefined };
  // Static rendering preserves covered panels without an active focus trap.
  const dialogRenderProps = stack.isEnabled
    ? { static: true }
    : { unmount: false };

  const context = useMemo<ModalContextState>(
    () => ({
      onClose,
      scrollable: scrollable || type === "overlay",
      portalRoot,
    }),
    [onClose, scrollable, type, portalRoot],
  );

  return (
    <Transition
      appear
      unmount={unmount}
      show={show}
      as={Fragment}
      afterLeave={() => {
        if (show) return;
        stack.completeClose();
        onAfterClose?.();
      }}
    >
      <Dialog
        open={stack.isEnabled ? stack.isTopmost : undefined}
        {...dialogRenderProps}
        onClose={() => !isInactive && onClose?.()}
        {...inertProps}
        aria-hidden={isInactive || undefined}
        ref={ref}
        initialFocus={initialRef}
        className={clsx("relative z-20", isHiddenByStack && "hidden")}
        style={
          stack.isEnabled ? { zIndex: 21 + stack.position * 2 } : undefined
        }
      >
        <ModalContext.Provider value={context}>
          {!stack.isEnabled && (
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/75" data-modal-backdrop />
            </Transition.Child>
          )}
          <div
            className={clsx("fixed inset-0 flex", {
              "justify-center": type === "modal",
              "justify-end": type === "overlay",
              "items-center": centered && type === "modal",
              "overflow-y-auto": !scrollable && type === "modal",
            })}
          >
            <div
              className={clsx("w-full", {
                "p-8": type === "modal",
                "max-w-md": size === "sm",
                "max-w-xl": size === "md",
                "max-w-4xl": size === "lg",
                "max-w-7xl": size === "xl",
                "max-w-full": size === "full",
              })}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom={
                  type === "modal"
                    ? "opacity-0 scale-95"
                    : "opacity-0 translate-x-full"
                }
                enterTo={
                  type === "modal"
                    ? "opacity-100 scale-100"
                    : "opacity-100 translate-x-0"
                }
                leave="ease-in duration-200"
                leaveFrom={
                  type === "modal"
                    ? "opacity-100 scale-100"
                    : "opacity-100 translate-x-0"
                }
                leaveTo={
                  type === "modal"
                    ? "opacity-0 scale-95"
                    : "opacity-0 translate-x-full"
                }
              >
                <Dialog.Panel
                  as={Card}
                  {...rest}
                  ref={initialRef}
                  hover={false}
                  className={twMerge(
                    clsx("w-full", {
                      "rounded-none": type === "overlay",
                      "overflow-y-hidden flex flex-col":
                        scrollable || type === "overlay",
                      "max-h-full": scrollable && type === "modal",
                      "h-full": type === "overlay",
                    }),
                    className,
                  )}
                />
              </Transition.Child>
            </div>
          </div>
          <Portal>
            <div ref={setPortalRoot} data-modal-portal />
          </Portal>
        </ModalContext.Provider>
      </Dialog>
    </Transition>
  );
});

export default Modal;
